import {
  durationUnitsFromAbcTok,
  expandFigure,
  figureLabel,
  type RhythmFigureId,
} from './rhythmTokens';

export type TimeSignature = '2/4' | '3/4' | '4/4';

export type EcoSoundId = 'clap' | 'snare' | 'shaker';

export const MAX_MEASURES = 4;
/** Compases visibles por fila del tren (cada compás = N vagones). */
export const MEASURES_PER_TRAIN_ROW = 2;

export const ECO_SOUND_PATHS: Record<EcoSoundId, string> = {
  clap: '/junior-assets/sounds/clap.wav',
  snare: '/junior-assets/sounds/snare.wav',
  shaker: '/junior-assets/sounds/shaker.wav',
};

export const ECO_SOUND_META: Record<EcoSoundId, { label: string; emoji: string }> = {
  clap: { label: 'Clap', emoji: '👏' },
  snare: { label: 'Caja', emoji: '🥁' },
  shaker: { label: 'Shaker', emoji: '🪇' },
};

/** Vagones por compás según el compás musical. 4/4 → 4 vagones = 1 compás. */
export function slotsPerMeasure(timeSignature: TimeSignature): number {
  switch (timeSignature) {
    case '2/4':
      return 2;
    case '3/4':
      return 3;
    case '4/4':
    default:
      return 4;
  }
}

export function measureUnitsForTimeSignature(ts: TimeSignature): number {
  switch (ts) {
    case '2/4':
      return 4;
    case '3/4':
      return 6;
    case '4/4':
    default:
      return 8;
  }
}

export function figureDurationUnits(fig: RhythmFigureId): number {
  return expandFigure(fig).reduce(
    (sum, atom) => sum + (durationUnitsFromAbcTok(atom.abcTok) ?? 0),
    0
  );
}

export function measureUnits(figures: RhythmFigureId[]): number {
  return figures.reduce((sum, f) => sum + figureDurationUnits(f), 0);
}

/** Una fila de vagones (slots) por compás; cada slot = una figura o vacío. */
export type MeasureSlots = (RhythmFigureId | null)[];
export type SlotPattern = MeasureSlots[];

export function emptyMeasureSlots(timeSignature: TimeSignature): MeasureSlots {
  return Array.from({ length: slotsPerMeasure(timeSignature) }, () => null);
}

export function emptyPattern(measureCount: number, timeSignature: TimeSignature): SlotPattern {
  return Array.from({ length: measureCount }, () => emptyMeasureSlots(timeSignature));
}

export function measureFiguresFromSlots(slots: MeasureSlots): RhythmFigureId[] {
  return slots.filter((s): s is RhythmFigureId => s != null);
}

export function isValidMeasureSlots(slots: MeasureSlots, timeSignature: TimeSignature): boolean {
  return measureUnits(measureFiguresFromSlots(slots)) === measureUnitsForTimeSignature(timeSignature);
}

export function flattenPattern(pattern: SlotPattern): RhythmFigureId[] {
  return pattern.flatMap((m) => measureFiguresFromSlots(m));
}

export interface BuilderBlockDef {
  figureId: RhythmFigureId;
  label: string;
  color: string;
  bg: string;
  border: string;
}

/** Acentos por compás (bordes punteados alegres). */
export const MEASURE_ACCENTS = [
  '#5B9BD5',
  '#F59E0B',
  '#10B981',
  '#A855F7',
] as const;

export const BUILDER_BLOCKS: BuilderBlockDef[] = [
  { figureId: 'negra', label: 'Negra', color: '#2563EB', bg: '#D1E5FF', border: '#A3C9FF' },
  { figureId: 'blanca', label: 'Blanca', color: '#EA580C', bg: '#FFE4C4', border: '#FFB366' },
  { figureId: 'corchea_doble', label: '2 Corcheas', color: '#059669', bg: '#A7F3D0', border: '#6EE7B7' },
  { figureId: 'cuartina', label: '4 Semicorcheas', color: '#DC2626', bg: '#FECACA', border: '#FCA5A5' },
  { figureId: 'silencio_negra', label: 'Silencio', color: '#64748B', bg: '#E2E8F0', border: '#CBD5E1' },
];

export interface EcoRitmoSessionConfig {
  measureCount: number;
  timeSignature: TimeSignature;
  bpm: number;
  soundId: EcoSoundId;
  pattern: SlotPattern;
}

export interface WagonRef {
  measureIndex: number;
  slotIndex: number;
}

export function wagonRef(measureIndex: number, slotIndex: number): WagonRef {
  return { measureIndex, slotIndex };
}

export function measureBreaksFromPattern(pattern: SlotPattern): number[] {
  const breaks: number[] = [];
  let cells = 0;
  for (let i = 0; i < pattern.length - 1; i++) {
    cells += measureFiguresFromSlots(pattern[i] ?? []).length;
    if (cells > 0) breaks.push(cells - 1);
  }
  return breaks;
}

export function isPatternComplete(pattern: SlotPattern, timeSignature: TimeSignature): boolean {
  return pattern.length > 0 && pattern.every((m) => isValidMeasureSlots(m, timeSignature));
}

export interface EcoRitmoExercise {
  id: string;
  title: string;
  hint: string;
  figures: RhythmFigureId[];
  bpm: number;
  passScore?: number;
  measureCount: number;
  pattern: SlotPattern;
  timeSignature: TimeSignature;
  soundId: EcoSoundId;
}

export function buildExerciseFromConfig(config: EcoRitmoSessionConfig): EcoRitmoExercise {
  return {
    id: `eco-${Date.now()}`,
    title: `Mi eco · ${config.timeSignature}`,
    hint: 'Escucha el patrón del tren y repítelo.',
    figures: flattenPattern(config.pattern),
    bpm: config.bpm,
    passScore: 60,
    measureCount: config.measureCount,
    pattern: config.pattern,
    timeSignature: config.timeSignature,
    soundId: config.soundId,
  };
}

export function createDefaultSessionConfig(): EcoRitmoSessionConfig {
  return {
    measureCount: 1,
    timeSignature: '4/4',
    bpm: 80,
    soundId: 'clap',
    pattern: emptyPattern(1, '4/4'),
  };
}

export function cellRangeForMeasure(
  pattern: SlotPattern,
  measureIndex: number
): { start: number; count: number } {
  let start = 0;
  for (let i = 0; i < measureIndex; i++) {
    start += measureFiguresFromSlots(pattern[i] ?? []).length;
  }
  const count = measureFiguresFromSlots(pattern[measureIndex] ?? []).length;
  return { start, count };
}

export function measureIndexForCell(pattern: SlotPattern, cellIndex: number): number {
  let count = 0;
  for (let i = 0; i < pattern.length; i++) {
    const n = measureFiguresFromSlots(pattern[i] ?? []).length;
    count += n;
    if (cellIndex < count) return i;
  }
  return Math.max(0, pattern.length - 1);
}

/** Índice de celda de timeline → vagón (compás + slot). */
export function wagonRefForCellIndex(
  pattern: SlotPattern,
  cellIndex: number
): WagonRef | null {
  let idx = 0;
  for (let mi = 0; mi < pattern.length; mi++) {
    const slots = pattern[mi] ?? [];
    for (let si = 0; si < slots.length; si++) {
      if (slots[si] != null) {
        if (idx === cellIndex) return wagonRef(mi, si);
        idx++;
      }
    }
  }
  return null;
}

/** Vagón → índice de celda en la timeline. */
export function cellIndexForWagonRef(
  pattern: SlotPattern,
  measureIndex: number,
  slotIndex: number
): number {
  let idx = 0;
  for (let mi = 0; mi < pattern.length; mi++) {
    const slots = pattern[mi] ?? [];
    for (let si = 0; si < slots.length; si++) {
      if (slots[si] != null) {
        if (mi === measureIndex && si === slotIndex) return idx;
        idx++;
      }
    }
  }
  return -1;
}

export function canPlaceInSlot(
  slots: MeasureSlots,
  slotIndex: number,
  fig: RhythmFigureId,
  timeSignature: TimeSignature
): boolean {
  const next = [...slots];
  next[slotIndex] = fig;
  return measureUnits(measureFiguresFromSlots(next)) <= measureUnitsForTimeSignature(timeSignature);
}

export function setSlotFigure(
  pattern: SlotPattern,
  ref: WagonRef,
  fig: RhythmFigureId | null,
  timeSignature: TimeSignature
): SlotPattern | null {
  const slots = [...(pattern[ref.measureIndex] ?? emptyMeasureSlots(timeSignature))];
  if (fig != null && !canPlaceInSlot(slots, ref.slotIndex, fig, timeSignature)) return null;
  slots[ref.slotIndex] = fig;
  return pattern.map((m, i) => (i === ref.measureIndex ? slots : m));
}

export function blockDefForFigure(fig: RhythmFigureId): BuilderBlockDef {
  return (
    BUILDER_BLOCKS.find((b) => b.figureId === fig) ?? {
      figureId: fig,
      label: figureLabel(fig),
      color: '#6B7280',
      bg: '#F3F4F6',
      border: '#CBD5E1',
    }
  );
}

export function clampMeasureCount(n: number): number {
  return Math.max(1, Math.min(MAX_MEASURES, n));
}

export function resizePatternForTimeSignature(
  pattern: SlotPattern,
  timeSignature: TimeSignature
): SlotPattern {
  return pattern.map(() => emptyMeasureSlots(timeSignature));
}

export function isMeasureComplete(slots: MeasureSlots, timeSignature: TimeSignature): boolean {
  return isValidMeasureSlots(slots, timeSignature);
}

export function totalWagonCount(measureCount: number, timeSignature: TimeSignature): number {
  return measureCount * slotsPerMeasure(timeSignature);
}
